import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import CoverChapter from "../components/chapters/CoverChapter";
import { OpeningChapter, PhilosophyChapter, ValuesChapter, ManifestoChapter, MirroredRChapter, VoiceChapter, LogoChapter } from "../components/chapters/PartOne";
import { PaletteChapter, TypographyChapter, PhotographyChapter, PackagingChapter, ProductChapter } from "../components/chapters/PartTwo";
import { StorytellingChapter, ExperienceChapter, WebsiteChapter, SocialChapter, RetailChapter, RulesChapter, ClosingChapter, ContactChapter } from "../components/chapters/PartThree";
import TopNavigation from "../components/TopNavigation";
import ChapterIndex from "../components/ChapterIndex";
import Marquee from "../components/Marquee";
import SmoothScroll from "../components/SmoothScroll";
import { useMode } from "../lib/ModeContext";
import { CHAPTERS } from "../lib/content";
import { TID } from "../lib/testIds";
import Flipbook from "./Flipbook";

const ORDERED_CHAPTERS = [
    { slug: "cover", Component: CoverChapter },
    { slug: "opening", Component: OpeningChapter },
    { slug: "philosophy", Component: PhilosophyChapter },
    { slug: "values", Component: ValuesChapter },
    { slug: "manifesto", Component: ManifestoChapter },
    { slug: "mirrored-r", Component: MirroredRChapter },
    { slug: "tone-of-voice", Component: VoiceChapter },
    { slug: "logo", Component: LogoChapter },
    { slug: "palette", Component: PaletteChapter },
    { slug: "typography", Component: TypographyChapter },
    { slug: "photography", Component: PhotographyChapter },
    { slug: "packaging", Component: PackagingChapter },
    { slug: "product", Component: ProductChapter },
    { slug: "storytelling", Component: StorytellingChapter },
    { slug: "experience", Component: ExperienceChapter },
    { slug: "website", Component: WebsiteChapter },
    { slug: "social", Component: SocialChapter },
    { slug: "retail", Component: RetailChapter },
    { slug: "rules", Component: RulesChapter },
    { slug: "closing", Component: ClosingChapter },
    { slug: "contact", Component: ContactChapter },
];

export default function BrandBook() {
    const { mode } = useMode();
    const [indexOpen, setIndexOpen] = useState(false);
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.5 });

    const gotoSlug = (slug) => {
        const el = document.getElementById(slug);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleBeginScroll = () => {
        const el = document.getElementById("opening");
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    const handleExportPdf = () => {
        window.print();
    };

    if (mode === "flipbook") {
        return (
            <>
                <TopNavigation onOpenIndex={() => setIndexOpen(true)} onExportPdf={handleExportPdf} />
                <ChapterIndex open={indexOpen} onClose={() => setIndexOpen(false)} onGoto={() => setIndexOpen(false)} />
                <Flipbook chapters={ORDERED_CHAPTERS} />
            </>
        );
    }

    return (
        <SmoothScroll enabled>
            <div className="grain relative">
                <TopNavigation onOpenIndex={() => setIndexOpen(true)} onExportPdf={handleExportPdf} />
                <ChapterIndex open={indexOpen} onClose={() => setIndexOpen(false)} onGoto={gotoSlug} />

                {/* Progress bar */}
                <motion.div
                    data-testid={TID.progressBar}
                    className="fixed top-[62px] md:top-[70px] left-0 right-0 h-px origin-left z-40 no-print"
                    style={{ background: "#C9A96A", scaleX: progress }}
                />

                <main>
                    <CoverChapter onBegin={handleBeginScroll} />

                    <OpeningChapter />

                    {/* Slow editorial marquee — signature element */}
                    <Marquee text={"INTOLLERANTIA · MAISON ITALIANA · EDIZIONI RARE"} slow />

                    <PhilosophyChapter />
                    <ValuesChapter />
                    <ManifestoChapter />
                    <MirroredRChapter />
                    <VoiceChapter />
                    <LogoChapter />
                    <PaletteChapter />
                    <TypographyChapter />
                    <PhotographyChapter />

                    <Marquee text={"MENO QUANTITÀ · PIÙ VALORE · LESS QUANTITY · MORE VALUE"} dark />

                    <PackagingChapter />
                    <ProductChapter />
                    <StorytellingChapter />
                    <ExperienceChapter />
                    <WebsiteChapter />
                    <SocialChapter />
                    <RetailChapter />
                    <RulesChapter />
                    <ClosingChapter />
                    <ContactChapter />

                    {/* Colophon */}
                    <footer className="paper py-16 border-t" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <div className="column-12">
                            <div className="col-span-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em" }}>
                                    © MMXXVII · INTOLLERANTIA®
                                </div>
                                <div className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                    Vol. I — <span style={{ transform: "scaleX(-1)", display: "inline-block" }}>R</span>
                                </div>
                                <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.4em" }}>
                                    BRAND BOOK · EDITION 001 / 001
                                </div>
                            </div>
                        </div>
                    </footer>
                </main>
            </div>
        </SmoothScroll>
    );
}
